import { useEffect, useState } from 'react';
import { api } from '../api/client';
import { getPose } from '../data/poses';
import { formatDuration, totalSeconds, type RoutinesApi } from '../hooks/useRoutines';
import type { ProgressApi } from '../hooks/useProgress';
import { primeSpeech } from '../audio/announcer';
import { primeAudio } from '../audio/clipAnnouncer';
import { RoutinePlayerPage } from './RoutinePlayerPage';
import type { Routine } from '../types';

interface Props {
  id: string;
  routines: RoutinesApi;
  progress: ProgressApi;
}

type Load = 'loading' | 'notfound' | 'ready';

export function PublicRoutinePage({ id, routines, progress }: Props) {
  const [state, setState] = useState<Load>('loading');
  const [routine, setRoutine] = useState<Routine | null>(null);
  const [playing, setPlaying] = useState(false);
  const [savedId, setSavedId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setState('loading');
    setPlaying(false);
    setSavedId(null);
    api
      .getPublicRoutine(id)
      .then((r) => {
        if (cancelled) return;
        setRoutine(r);
        setState(r ? 'ready' : 'notfound');
      })
      .catch(() => {
        if (!cancelled) setState('notfound');
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  if (state === 'loading') {
    return (
      <div className="empty">
        <p>Loading shared routine…</p>
      </div>
    );
  }

  if (state === 'notfound' || !routine) {
    return (
      <div className="empty">
        <p>This shared routine isn't available — the link may be wrong, or sharing was turned off.</p>
        <a className="btn primary" href="#/">
          Browse poses
        </a>
      </div>
    );
  }

  if (playing) {
    return (
      <RoutinePlayerPage
        routines={routines}
        progress={progress}
        routineId={routine.id}
        routine={routine}
        exitHref={`#/r/${routine.id}`}
      />
    );
  }

  const names = routine.steps
    .map((s) => getPose(s.poseId)?.nameEnglish)
    .filter(Boolean)
    .join(' · ');

  return (
    <>
      <div className="page-head">
        <h1 className="page-title">{routine.name}</h1>
        <span className="shared-badge">Shared routine</span>
      </div>
      <div className="routine-sub">
        {routine.steps.length} pose{routine.steps.length === 1 ? '' : 's'} ·{' '}
        {formatDuration(totalSeconds(routine))} total
      </div>
      {names && <p className="routine-poses">{names}</p>}

      <div className="public-actions">
        <button
          type="button"
          className="btn primary"
          onClick={() => {
            primeSpeech();
            primeAudio();
            setPlaying(true);
          }}
        >
          ▶ Play
        </button>
        {savedId ? (
          <a className="btn" href="#/routines">
            Saved ✓ — go to my routines
          </a>
        ) : (
          <button
            type="button"
            className="btn"
            onClick={() => setSavedId(routines.create(`${routine.name} (copy)`, routine.steps).id)}
          >
            Save a copy
          </button>
        )}
      </div>

      <p className="hint">
        Someone shared this routine with you. Play it as-is, or save your own copy to tweak.
      </p>
    </>
  );
}
